import { all, call, fork, put, takeEvery, take } from "redux-saga/effects";
import { END, eventChannel } from "redux-saga";

import {
  createRewardError,
  createRewardSuccess,
  getRewardError,
  getRewardSuccess,
  getListRewardsError,
  getListRewardsSuccess,
  removeRewardError,
  removeRewardSuccess,
  updateRewardError,
  updateRewardSuccess,
  getCoursesForRewardOptionSuccess,
  getCoursesForRewardOptionError,
} from "./actions";
import {
  CREATE_REWARD,
  GET_REWARD,
  GET_LIST_REWARD,
  REMOVE_REWARD,
  UPDATE_REWARD,
  GET_COURSES_FOR_REWARD_OPTION,
} from "./actionTypes";
import FilesService from "../../../services/FilesService";
import {
  uploadFailure,
  uploadInitiate,
  uploadProgress,
  uploadSuccess,
} from "../../progress/actions";
import errorCodes from "../../../constants/errorCodes";
import throwCustomError from "../../../helpers/throwCustomError";
import RewardService from '../../../services/RewardService';
import CourseService from '../../../services/CourseService';
import { MAX_COURSES_LIMIT } from '../../../constants/courses';

const getRewardListAsync = async (data) => {
  const { model } = data;
  return await RewardService.getList(model);
};

const getRewardAsync = async (id) => {
  return await RewardService.getOne(id);
};

const rewardComposite = (model) => {
  const {
    title,
    subTitle,
    description,
    benefitDescription,
    firstButtonText,
    firstButtonLink,
    secondButtonText,
    secondButtonLink,
    files,
    companyLogoFiles,
    isHidden,
    isRedeemable,
    toApplySubscriptionConstraint,
    toApplyCourseConstraint,
    ordinalNumber,
    redemptionCodeSource,
    redemptionCodeType,
    courseIds
  } = model;

  return {
    files,
    companyLogoFiles,
    rewardData: {
      title,
      subTitle,
      description,
      benefitDescription,
      firstButtonText,
      firstButtonLink,
      secondButtonText,
      secondButtonLink,
      isHidden,
      isRedeemable,
      toApplySubscriptionConstraint,
      toApplyCourseConstraint,
      ordinalNumber,
      redemptionCodeSource,
      redemptionCodeType,
      courseIds
    },
  };
};

const getFilesArray = (files, companyLogoFiles) => {
  if (
    !!files &&
    !!files.length &&
    !!companyLogoFiles &&
    !!companyLogoFiles.length
  ) {
    return [...files, ...companyLogoFiles];
  } else if (!!companyLogoFiles && !!companyLogoFiles.length) {
    return companyLogoFiles;
  } else if (!!files && !!files.length) {
    return files;
  }
};

const getFilesTotalSize = (files, companyLogoFiles) => {
  if (
    !!files &&
    !!files.length &&
    !!companyLogoFiles &&
    !!companyLogoFiles.length
  ) {
    return files[0].size + companyLogoFiles[0].size;
  } else if (!!companyLogoFiles && !!companyLogoFiles.length) {
    return companyLogoFiles[0].size;
  } else if (!!files && !!files.length) {
    return files[0].size;
  }
};

/**
 * Reward files channel for files uploading watching
 * @param data<Object>
 * @param mode<"create"|"update"> - mode of files progress channel (create by default)
 */
const rewardFilesChannel = (data, mode = "create") => {
  return eventChannel((emit) => {
    const { model } = data;
    const { files, companyLogoFiles, rewardData } = rewardComposite(model);
    const actionFunc =
      mode === "create"
        ? RewardService.create(rewardData)
        : RewardService.update(data.rewardId, rewardData);
    if (
      (!files || !files.length) &&
      (!companyLogoFiles || !companyLogoFiles.length)
    ) {
      // here is only one file and it's mandatory
      actionFunc
        .then((result) => {
          emit({ success: result });
          emit(END);
        })
        .catch((err) => {
          emit({ err });
          emit(END);
        });
      return () => {};
    } else {
      const onProgress = ({ loaded }, file, chunkIndex) => {
        emit({ file, loaded, chunkIndex });
      };
      let reward;
      actionFunc
        .then(async ({ data }) => {
          reward = data;
          emit({ 
            files: getFilesArray(files, companyLogoFiles),
            filesTotalSize: getFilesTotalSize(files, companyLogoFiles) });
          const filesResponse = !!files && !!files.length && await FilesService.uploadImageFile({ files }, onProgress).catch(
            (error) => {
              throwCustomError(error);
            }
          );
          const companyLogoFilesResponse = !!companyLogoFiles && !!companyLogoFiles.length && await FilesService.uploadImageFile(
            { files: companyLogoFiles },
            onProgress
          ).catch((error) => {
            throwCustomError(error);
          });

          return { filesResponse, companyLogoFilesResponse };

          // const videoThumbnaiResponse = await FilesService.uploadImageFile(
          //   { files: videoThumbnail },
          //   onProgress
          // ).catch((error) => {
          //   throwCustomError(error);
          // });
          // return { filesResponse, videoThumbnaiResponse };
        })
        .then(async ({ filesResponse, companyLogoFilesResponse }) => {
          let addImageToRewardFunc;
          let addCompanyLogoImageToRewardFunc;
          if (!!filesResponse && !!filesResponse.length) {
            addImageToRewardFunc = RewardService.addImageToReward(reward.id, {
              fileId: filesResponse[0].file.id,
            }).catch((error) => {
              throwCustomError(error);
            });
          } 

          if (
            !!companyLogoFilesResponse &&
            !!companyLogoFilesResponse.length
          ) {
            addCompanyLogoImageToRewardFunc = RewardService.addCompanyLogoImageToReward(
              reward.id,
              {
                fileId: companyLogoFilesResponse[0].file.id,
              }
            ).catch((error) => {
              throwCustomError(error);
            });
          } 

          // return Promise.all([addImageToRewardFunc, addCompanyLogoImageToRewardFunc]);

          if (addImageToRewardFunc && addCompanyLogoImageToRewardFunc) {
            await addImageToRewardFunc;
            return addCompanyLogoImageToRewardFunc;
          } else if (addImageToRewardFunc) {
            return addImageToRewardFunc;
          } else if (addCompanyLogoImageToRewardFunc) {
            return addCompanyLogoImageToRewardFunc;
          }

          // await RewardService.addImageToReward(reward.id, {
          //   fileId: filesResponse[0].file.id,
          // }).catch((error) => {
          //   throwCustomError(error);
          // });

          // return RewardService.addCompanyLogoImageToReward(reward.id, {
          //   fileId: companyLogoFilesResponse[0].file.id,
          // }).catch((error) => {
          //   throwCustomError(error);
          // });
        })
        .then((result) => {
          emit({ success: result });
          emit(END);
        })
        .catch((err) => {
          emit({ err });
          emit(END);
        });
      return () => {};
    }
  });
};

const removeRewardAsync = async (id) => {
  return await RewardService.remove(id);
};

const getCoursesForRewardOptionListAsync = async (coursesCount = 0, coursesList = []) => {

  const { pagination: { totalCount }, data } = await CourseService.getAllCoursesList({
    limit: MAX_COURSES_LIMIT,
    offset: coursesCount,
  });
  coursesList = [...coursesList, ...data.map(({ id, title }) => ({ id, title }))]

  if (totalCount > coursesList.length) {
    coursesList = await getCoursesForRewardOptionListAsync(coursesList.length, coursesList)
  }

  return coursesList;
};

function* getRewardList({ payload }) {
  try {
    const response = yield call(getRewardListAsync, payload);
    yield put(getListRewardsSuccess(response));
  } catch (error) {
    yield put(getListRewardsError(error));
  }
}

function* getReward({ payload: { rewardId } }) {
  try {
    const response = yield call(getRewardAsync, rewardId);
    yield put(getRewardSuccess(response));
  } catch (error) {
    yield put(getRewardError(error));
  }
}

function* createRewardProgressListener(chan, history) {
  while (true) {
    const { files, filesTotalSize, file, loaded, err, success } =
      yield take(chan);
    if (!!files && !!filesTotalSize) {
      yield put(uploadInitiate({ files, filesTotalSize }));
    }
    if (err) {
      if (err.code === errorCodes.BAD_REQUEST_ERROR) {
        yield put(createRewardError(err));
      } else {
        yield put(uploadFailure(err));
        yield put(createRewardError(err));
        // history.push(`/rewards`);
      }
      return;
    }
    if (success) {
      yield put(uploadSuccess(success));
      yield put(createRewardSuccess(success));
      history.push(`/rewards`);
      return;
    }
    if (!!file && !!loaded) {
      yield put(uploadProgress({ file, loaded }));
    }
  }
}

function* createReward({ payload: { data, history } }) {
  try {
    yield fork(
      createRewardProgressListener,
      rewardFilesChannel(data),
      history
    );
  } catch (error) {
    yield put(createRewardError(error));
  }
}

function* updateRewardProgressListener(chan, history) {
  while (true) {
    const { files, filesTotalSize, file, loaded, chunkIndex, err, success } =
      yield take(chan);
    if (!!files && !!filesTotalSize) {
      yield put(uploadInitiate({ files, filesTotalSize }));
    }
    if (err) {
      if (err.code === errorCodes.BAD_REQUEST_ERROR) {
        yield put(updateRewardError(err));
      } else {
        yield put(uploadFailure(err));
        yield put(updateRewardError(err));
        // history.push(`/rewards`);
      }
      return;
    }
    if (success) {
      yield put(uploadSuccess(success));
      yield put(updateRewardSuccess(success));
      history.push(`/rewards`);
      return;
    }
    if (!!file && !!loaded) {
      yield put(uploadProgress({ file, loaded, chunkIndex }));
    }
  }
}

function* updateReward({ payload: { data, history } }) {
  try {
    yield fork(
      updateRewardProgressListener,
      rewardFilesChannel(data, "update"),
      history,
    );
  } catch (error) {
    yield put(updateRewardError(error));
  }
}

function* removeReward({ payload: { data } }) {
  try {
    const response = yield call(removeRewardAsync, data);
    yield put(removeRewardSuccess(response));
  } catch (error) {
    yield put(removeRewardError(error));
  }
}

function* getCoursesForRewardOption({payload: { limit = 0, offset = 0 }}) {
  try {
    const response = yield call(getCoursesForRewardOptionListAsync);
    yield put(getCoursesForRewardOptionSuccess(response));
  } catch (error) {
    yield put(getCoursesForRewardOptionError(error))
  }
}

export function* watchGetRewardList() {
  yield takeEvery(GET_LIST_REWARD, getRewardList);
}

export function* watchCreateReward() {
  yield takeEvery(CREATE_REWARD, createReward);
}

export function* watchUpdateReward() {
  yield takeEvery(UPDATE_REWARD, updateReward);
}

export function* watchRemoveReward() {
  yield takeEvery(REMOVE_REWARD, removeReward);
}

export function* watchGetReward() {
  yield takeEvery(GET_REWARD, getReward);
}

export function* watchGetCoursesForRewardOption() {
  yield takeEvery(GET_COURSES_FOR_REWARD_OPTION, getCoursesForRewardOption)
}

function* coursesSaga() {
  yield all([
    fork(watchGetRewardList),
    fork(watchCreateReward),
    fork(watchUpdateReward),
    fork(watchRemoveReward),
    fork(watchGetReward),
    fork(watchGetCoursesForRewardOption)
  ]);
}

export default coursesSaga;
